// SPDX-License-Identifier: AGPL-3.0-or-later

import type {HolidayPackId} from '@app/features/holiday/state/HolidayPreferences';
import {resolveHolidaySeasonWindow} from '@app/features/holiday/utils/holidaySeason';

/** Intensity at `peakStart` when a caller does not pin its own waypoint. */
export const DEFAULT_HOLIDAY_PEAK_ANCHOR = 0.2;

export interface HolidayIntensityOptions {
	/** Value the ramp reaches at `peakStart`. Effects that pin it to their own tuning pass it here. */
	peakAnchor?: number;
}

/** Position inside the current phase, for effects that ramp on a different easing than `holidayIntensity`. */
export interface HolidaySeasonRamp {
	phase: 'preseason' | 'peak';
	/** 0 at the phase start, approaching 1 at its end. `seasonEnd` is exclusive, so 1 is never reached. */
	progress: number;
}

function clamp01(value: number): number {
	if (!Number.isFinite(value)) return 0;
	return Math.min(1, Math.max(0, value));
}

function spanProgress(from: Date, to: Date, now: Date): number {
	const span = to.getTime() - from.getTime();
	if (span <= 0) return 1;
	return clamp01((now.getTime() - from.getTime()) / span);
}

/** Phase plus position within it, or null out of season. Calendar only, no preferences. */
export function holidaySeasonRamp(packId: HolidayPackId, now: Date = new Date()): HolidaySeasonRamp | null {
	const resolved = resolveHolidaySeasonWindow(packId, now);
	if (resolved == null) return null;
	if (resolved.phase === 'preseason') {
		return {phase: 'preseason', progress: spanProgress(resolved.preseasonStart, resolved.peakStart, now)};
	}
	return {phase: 'peak', progress: spanProgress(resolved.peakStart, resolved.seasonEnd, now)};
}

/**
 * Season-scaled 0→1 ramp for effects whose frequency grows toward the end of a pack's window:
 * quadratic ease-in across preseason up to `peakAnchor`, cubic ease-in across peak up to 1.
 *
 * Returns 0 out of season. Callers still gate on season and preferences separately — this is
 * calendar math only, and knows nothing about chat, particles, or any surface.
 */
export function holidayIntensity(
	packId: HolidayPackId,
	now: Date = new Date(),
	options?: HolidayIntensityOptions,
): number {
	const ramp = holidaySeasonRamp(packId, now);
	if (ramp == null) return 0;
	const peakAnchor = clamp01(options?.peakAnchor ?? DEFAULT_HOLIDAY_PEAK_ANCHOR);
	if (ramp.phase === 'preseason') {
		return peakAnchor * ramp.progress * ramp.progress;
	}
	return peakAnchor + (1 - peakAnchor) * ramp.progress * ramp.progress * ramp.progress;
}
